import {
  Boxes,
  Container,
  Database,
  Gauge,
  Globe,
  HardDrive,
  Mail,
  Network,
  Server,
  Settings2,
  Users,
  type LucideIcon,
} from "lucide-react";

// categories.ts — the nav group labels + icons for module.Manifest.Category (V6.7b's backend
// field), reusing V6.7e's own concrete menu map. Order is the sidebar's group order.

export const CATEGORY_ORDER = [
  "accounts",
  "nodes",
  "mail",
  "database",
  "web",
  "network",
  "storage",
  "containers",
  "diagnostics",
  "platform",
] as const;

export const CATEGORY_LABELS: Record<string, string> = {
  accounts: "Accounts",
  nodes: "Nodes & cluster",
  mail: "Mail",
  database: "Databases",
  web: "Web & domains",
  network: "Network & security",
  storage: "Storage",
  containers: "Containers",
  diagnostics: "Diagnostics",
  platform: "Platform",
};

// Icons make a 47-item nav scannable — the eye finds a shape far faster than it reads a word,
// which is the whole problem with a flat alphabetical text list this long.
export const CATEGORY_ICONS: Record<string, LucideIcon> = {
  accounts: Users,
  nodes: Server,
  mail: Mail,
  database: Database,
  web: Globe,
  network: Network,
  storage: HardDrive,
  containers: Container,
  diagnostics: Gauge,
  platform: Settings2,
};

export function categoryLabel(category: string | undefined): string {
  if (!category) return "Uncategorized";
  return CATEGORY_LABELS[category] ?? category;
}

export function categoryIcon(category: string | undefined): LucideIcon {
  return (category && CATEGORY_ICONS[category]) || Boxes;
}

// STATIC_NAV_ITEMS — real screens that are NOT `module.Module`s and so never appear in
// GET /ozpanel/modules (tenants/domains/plans live in internal/server/*.go, predating the
// module system). Merged into the same grouped structure as registry-driven modules.
export interface StaticNavItem {
  key: string;
  label: string;
  path: string;
  category: string;
  roles: string[];
}

export const STATIC_NAV_ITEMS: StaticNavItem[] = [
  { key: "tenants", label: "Tenants", path: "/tenants", category: "accounts", roles: ["super_admin", "domain_admin"] },
  // Plans decide what every account on them may do, so they belong beside the accounts rather
  // than buried in a tenant's detail page.
  { key: "plans", label: "Plans", path: "/plans", category: "accounts", roles: ["super_admin"] },
  { key: "domains", label: "Domains", path: "/domains", category: "web", roles: ["super_admin", "domain_admin"] },
  { key: "relays", label: "Relays & routing", path: "/mail/relays", category: "mail", roles: ["super_admin"] },
  { key: "platform-sender", label: "Platform sender", path: "/mail/platform-sender", category: "mail", roles: ["super_admin"] },
  // A screen that exists at a route nobody can navigate to is not shipped — it is the
  // "built capability nobody can reach" defect, and six screens were written before anyone
  // noticed that only the one backed by a MODULE (which gets its nav entry automatically from
  // the registry) was reachable at all. Every hand-written screen needs its entry HERE, in the
  // same change that adds its route.
  { key: "mail-overview", label: "Mail overview", path: "/mail", category: "mail", roles: ["super_admin"] },
  { key: "mail-domains", label: "Mail domains", path: "/mail/domains", category: "mail", roles: ["super_admin"] },
  { key: "updates", label: "Updates", path: "/updates", category: "nodes", roles: ["super_admin"] },
  { key: "role-transfer", label: "Role transfer", path: "/nodes/planes", category: "nodes", roles: ["super_admin"] },
  { key: "add-node", label: "Add a node", path: "/nodes/add", category: "nodes", roles: ["super_admin"] },
  { key: "storage", label: "Shared storage", path: "/storage", category: "nodes", roles: ["super_admin"] },
  { key: "app-events", label: "Container events", path: "/app-events", category: "accounts", roles: ["super_admin", "domain_admin"] },
  { key: "database", label: "Database", path: "/database", category: "platform", roles: ["super_admin"] },
  { key: "firewall", label: "Firewall", path: "/firewall", category: "network", roles: ["super_admin"] },
  { key: "port-requests", label: "Port requests", path: "/port-requests", category: "network", roles: ["super_admin"] },
  { key: "redis-requests", label: "Redis requests", path: "/redis-requests", category: "network", roles: ["super_admin"] },
  { key: "token-requests", label: "Token requests", path: "/token-requests", category: "network", roles: ["super_admin"] },
  { key: "api-tokens", label: "API tokens", path: "/api-tokens", category: "network", roles: ["super_admin"] },
  { key: "web-vhosts", label: "Vhosts & DNS", path: "/web", category: "web", roles: ["super_admin"] },
  { key: "dns-zones", label: "DNS zones", path: "/dns/zones", category: "web", roles: ["super_admin"] },
  { key: "platform", label: "Certificates & audit", path: "/platform", category: "platform", roles: ["super_admin"] },
  { key: "tunables", label: "Tunables", path: "/tunables", category: "diagnostics", roles: ["super_admin"] },
];
